// hooks/billing/useOrgBalance.js
import { useCallback, useEffect, useState } from 'react';
import BillingService from '../../services/billing.service';

/**
 * useOrgBalance — `GET /organizations/{orgId}/billing/balance`.
 *
 * Only resolves for orgs on an Enterprise subscription; otherwise the
 * backend responds 404 NOT_AN_ENTERPRISE_ORG. The caller branches on
 * `error.code === 'NOT_AN_ENTERPRISE_ORG'` to render a neutral empty
 * state ("not on Enterprise plan") rather than a hard error.
 *
 * @param {string} organizationId
 * @param {Object} [options]
 * @param {boolean} [options.auto=true]
 */
export function useOrgBalance(organizationId, { auto = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(auto && organizationId));
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    const result = await BillingService.getOrgBalance(organizationId);
    if (result.success) {
      setData(result.data);
    } else {
      setError(result);
    }
    setLoading(false);
  }, [organizationId]);

  useEffect(() => {
    if (auto && organizationId) {
      refetch();
    } else if (!organizationId) {
      setData(null);
      setError(null);
      setLoading(false);
    }
  }, [auto, organizationId, refetch]);

  return { data, loading, error, refetch };
}

export default useOrgBalance;
